import React from "react";
import { getTheme } from "lib/theme";
import { Card } from "react-bootstrap";
import { apiVersion } from "settings";

export const lorem = () => {
  return (
    <p>
      Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod
      tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim
      veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea
      commodo consequat. Duis aute irure dolor in reprehenderit in voluptate
      velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat
      cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id
      est laborum.
    </p>
  );
};

export const userManagementOverview = () => {
  return (
    <p>
      This section describes how the application manages and groups users. More
      information about roles, permissions, and authorization can be found in
      the <b>Roles</b> section.
    </p>
  );
};

export const accountsDescription = () => {
  return (
    <p>
      An <b>Account</b> is a collection of users. Accounts can have:
      <ul>
        <li>
          zero or more <b>Owners</b> and zero or more <b>Users</b> (referred to
          as <b>Account Users</b> for clarity)
        </li>
        <li>zero or more <b>Analyses</b></li>
        <li>zero or more <b>Teams</b></li>
      </ul>
    </p>
  );
};

export const analysesDescription = () => {
  return (
    <p>
      Creating an <b>Analysis</b> is like creating a folder inside of a parent
      folder; where the parent folder is an <b>Account</b>. It's a place for an
      application <b>Administrator</b> to store important files, dashboards, and
      reports that members of that <b>Account</b> (the customer) can access securely.
    </p>
  );
};

export const teamsDescription = () => {
  return (
    <>
      <p>
        The main purpose of a <b>Team</b> is to define access to an{" "}
        <b>Analysis</b>. When an Analysis is created, by default, it is
        restricted to all Account Users other than the Owners of that Account.
      </p>
      <p>
        In order to grant access to an Analysis for one or more Account Users,
        an Account Owner must:
        <ul>
          <li>create a Team</li>
          <li>add the desired Account Users to that Team</li>
          <li>grant access to that Analysis for that Team</li>
        </ul>
      </p>
      <p>
        For example, suppose an organization has sensitive reports inside one of
        their Analyses (let's call the Analysis "Sales Report") and only want to
        allow access to their upper management. All organization employess can
        be added to the organization's Account without concern. An Account Owner
        can then create a new Team called "Management" and add the necessary
        Account Users to the "Management" Team. The Account Owner can then grant
        access to "Management" Team to view the "Sales Report" Analysis. Account Users that belong to the 
        "Mangement" Team will now be able to access the "Sales Report" Analysis.
      </p>
    </>
  );
};

export const accountUsersDescription = () => {
  return (
    <p>
      An <b>"Account User"</b> is a general <b>User</b> that has been added to an{" "}
      <b>Account</b>. A general User can belong to more than one <b>Account</b>{" "}
      and can be an <b>Owner</b> of more than one <b>Account</b>. Once added to
      an Account, an Account User will be able to see other Users that have been
      added to the same Account. Account Users can be belong to one or more{" "}
      <b>Teams</b>.
    </p>
  );
};

export const roleOverview = () => {
  return (
    <p>
      Role-based security is used to restrict application access to only
      authorized users. Rather than assigning and managing resource access to
      individual users, a user is assigned a <b>General Role</b> that defines
      their base permissions. A user can then be added to an Account and
      assigned an <b>Account Role</b> to extend their permissions.
    </p>
  );
};

export const generalRoleOverview = () => {
  return (
    <p>
      A user can be assigned one of the following general roles:{" "}
      <b>Administrator</b> and <b>Member</b>.
    </p>
  );
};

export const administratorRoleDescription = () => {
  return (
    <>
      <p>
        An <b>Administrator</b> has global read/write/execute priviliges across
        the entire application. Administrators can{" "}
        <span style={{ color: "blue", fontWeight: 600 }}>VIEW</span>,{" "}
        <span style={{ color: "green", fontWeight: 600 }}>CREATE</span>,{" "}
        <span style={{ color: "green", fontWeight: 600 }}>ADD</span>,{" "}
        <span style={{ color: "orange", fontWeight: 600 }}>MODIFY</span>,{" "}
        <span style={{ color: "red", fontWeight: 600 }}>REMOVE</span>, and{" "}
        <span style={{ color: "red", fontWeight: 600 }}>DELETE</span> the following resources:
        <ul>
          <li>Users</li>
          <li>Accounts</li>
          <li>Account Analyses</li>
          <li>Account Teams</li>
          <li>Configuration Keys</li>
          <li>External Links</li>
        </ul>
      </p>
      <p>
        The <b>Administrator</b> role is reserved for Avlino Management in order
        to maintain the web application and its underlying services for the
        customer.
      </p>
    </>
  );
};

export const memberRoleDescription = () => {
  return (
    <>
      <p>
        A <b>General Member</b> has, by default, limited read/write/execute
        priviliges. General Members have{" "}
        <span style={{ color: "blue", fontWeight: 600 }}>VIEW</span> permissions for all
        essential application features, but do not have any{" "}
        <span style={{ color: "green", fontWeight: 600 }}>CREATE</span>,{" "}
        <span style={{ color: "green", fontWeight: 600 }}>ADD</span>,{" "}
        <span style={{ color: "orange", fontWeight: 600 }}>MODIFY</span>,{" "}
        <span style={{ color: "red", fontWeight: 600 }}>REMOVE</span>,{" "}
        <span style={{ color: "red", fontWeight: 600 }}>DELETE</span> or{" "}
        <span style={{ color: "darkorange", fontWeight: 600 }}>EXECUTE</span> permissions.
      </p>
    </>
  );
};

export const accountRoleOverview = () => {
  return (
    <p>
      A user can be assigned one of the following Account roles: <b>Owner</b>{" "}
      and <b>Member</b>.
    </p>
  );
};

export const accountOwnerRoleDescription = () => {
  return (
    <>
      <p>
        An Account <b>Owner</b> has the second highest level of application
        access next to an <b>Administrator</b>. Account Owner is also the
        highest level of access a non-Administrator can be given.
      </p>
      <p>
        <b>Account Owners</b> can <span style={{ color: "green", fontWeight: 600 }}>CREATE</span>
        :
        <ul>
          <li>Users</li>
          <li>Teams in Accounts they own</li>
        </ul>
      </p>
      <p>
        <b>Account Owners</b> can <span style={{ color: "green", fontWeight: 600 }}>ADD</span>:
        <ul>
          <li>Users to Accounts they own</li>
          <li>Users to Teams within Accounts they own</li>
          <li>Teams to Analyses within Accounts they own</li>
        </ul>
      </p>
      <p>
        <b>Account Owners</b> can{" "}
        <span style={{ color: "darkorange", fontWeight: 600 }}>EXECUTE</span>:
        <ul>
          <li>YardSight ON/OFF</li>
          <li>Decking Optimization ON/OFF</li>
        </ul>
      </p>
      <p>
        <b>Account Owners</b> can <span style={{ color: "red", fontWeight: 600 }}>REMOVE</span>:
        <ul>
          <li>Users from Accounts they own</li>
          <li>Users from Teams within Accounts they own</li>
        </ul>
      </p>
      <p>
        <b>Account Owners</b> do not have any{" "}
        <span style={{ color: "red", fontWeight: 600 }}>DELETE</span> permissions.
      </p>
    </>
  );
};

export const accountMemberDescription = () => {
  return (
    <p>
      As of the current version of this application ({apiVersion}),{" "}
      <b>Account Members</b> have have no added permissions from the base{" "}
      <b>General Member</b> role.
    </p>
  );
};

export const teamRoleOverview = () => {
  return (
    <p>
      Once a user has been added to an Account, they can be assigned one of the
      following Team roles: <b>Leader</b> and <b>Member</b>.
    </p>
  );
};

export const teamLeaderDescription = () => {
  return (
    <>
      <p>
        As of the current version of this application ({apiVersion}), the{" "}
        <b>Team Leader</b> role is limited in its added permissions from the
        base <b>General Member</b> and <b>Account Member</b> roles.
      </p>
      <p>
        Team Leaders can <span style={{ color: "green", fontWeight: 600 }}>ADD</span>:
        <ul>
          <li>Users in their Account to their Team</li>
        </ul>
      </p>
    </>
  );
};

export const teamMemberDescription = () => {
  return (
    <>
      <p>
        As of the current version of this application ({apiVersion}), the{" "}
        <b>Team Member</b> role is limited in its added permissions from the
        base <b>General Member</b> and <b>Account Member</b> roles.
      </p>
    </>
  );
};
